import React, { useEffect, useRef, useState } from 'react';
import { CARD } from '../ui';
import { DATA_HUE, GRID, niceCeil, formatCount } from './theme';
import { formatDay } from './range';

const PLOT_HEIGHT = 180;

// Long ranges are bucketed rather than plotted a day at a time: 90 daily
// columns on a phone are about a pixel wide, and the alternative — a nested
// horizontal scrollbar inside the card — is worse than a coarser bucket.
const bucketDaily = (daily) => {
  if (daily.length <= 31) {
    return {
      unit: 'day',
      buckets: daily.map((day) => ({ key: day.date, start: day.date, end: day.date, count: day.count })),
    };
  }

  if (daily.length <= 120) {
    const buckets = [];
    for (let i = 0; i < daily.length; i += 7) {
      const week = daily.slice(i, i + 7);
      buckets.push({
        key: week[0].date,
        start: week[0].date,
        end: week[week.length - 1].date,
        count: week.reduce((sum, day) => sum + day.count, 0),
      });
    }
    return { unit: 'week', buckets };
  }

  const months = new Map();
  for (const day of daily) {
    const key = day.date.slice(0, 7);
    const bucket = months.get(key) || { key, start: day.date, end: day.date, count: 0 };
    bucket.end = day.date;
    bucket.count += day.count;
    months.set(key, bucket);
  }
  return { unit: 'month', buckets: [...months.values()] };
};

const UNIT_COPY = {
  day: { title: 'Submissions per day', column: 'Day' },
  week: { title: 'Submissions per week', column: 'Week starting' },
  month: { title: 'Submissions per month', column: 'Month' },
};

const axisLabel = (bucket, unit) =>
  unit === 'month' ? formatDay(bucket.start, { month: 'short' }) : formatDay(bucket.start);

const fullLabel = (bucket, unit) => {
  if (unit === 'day') return formatDay(bucket.start, { weekday: 'short', month: 'short', day: 'numeric' });
  if (unit === 'month') return formatDay(bucket.start, { month: 'long', year: 'numeric' });
  return `${formatDay(bucket.start)} – ${formatDay(bucket.end)}`;
};

// Thin out x labels so a month of days doesn't render 31 overlapping dates.
// Always keeps the first and last so the axis states its own bounds.
const tickIndexes = (length, maxTicks) => {
  if (length <= maxTicks) return [...Array(length).keys()];
  const step = (length - 1) / (maxTicks - 1);
  return [...new Set([...Array(maxTicks).keys()].map((i) => Math.round(i * step)))];
};

// A date label runs ~42px, so budget 70px of axis per tick. Measured rather
// than assumed: the same chart sits in a full-width card on a desktop and in a
// ~270px column on a phone, and a fixed tick count collides in the narrow one.
const LABEL_BUDGET = 70;
const MAX_TICKS = 7;

const useTickBudget = (enabled) => {
  const ref = useRef(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;

    // Measure once up front so the first paint is already correct. The window
    // listener covers the common case; ResizeObserver additionally catches the
    // card changing width without the window doing so (grid reflow).
    const measure = () => setWidth(node.getBoundingClientRect().width);
    measure();
    window.addEventListener('resize', measure);

    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    observer?.observe(node);
    return () => {
      window.removeEventListener('resize', measure);
      observer?.disconnect();
    };
  }, [enabled]);

  const maxTicks = width ? Math.max(2, Math.min(MAX_TICKS, Math.floor(width / LABEL_BUDGET))) : MAX_TICKS;
  return [ref, maxTicks];
};

const ActivityChart = ({ daily, subtitle }) => {
  const [active, setActive] = useState(null);
  const [asTable, setAsTable] = useState(false);

  const [plotRef, maxTicks] = useTickBudget(asTable);

  const { unit, buckets } = bucketDaily(daily);
  const copy = UNIT_COPY[unit];
  const peak = Math.max(0, ...buckets.map((b) => b.count));
  const axisMax = niceCeil(peak);
  const ticks = tickIndexes(buckets.length, maxTicks);
  const hovered = active !== null ? buckets[active] : null;
  // Keep the readout inside the plot when the hovered column is near an edge.
  const tooltipLeft =
    active === null ? 0 : Math.min(94, Math.max(6, ((active + 0.5) / buckets.length) * 100));

  return (
    <div className={`${CARD} p-6`}>
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h2 className="text-lg font-semibold text-white">{copy.title}</h2>
          {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
        </div>
        <button
          type="button"
          onClick={() => setAsTable((v) => !v)}
          className="bg-gray-700/50 hover:bg-gray-600/50 text-gray-300 hover:text-white px-3 py-1.5 rounded-lg text-xs font-medium transition-colors duration-200 border border-gray-600 hover:border-gray-500 flex-shrink-0"
        >
          {asTable ? 'Chart' : 'Table'}
        </button>
      </div>

      {asTable ? (
        <div className="max-h-64 overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-gray-800">
              <tr className="text-left text-gray-400">
                <th scope="col" className="py-2 font-medium">{copy.column}</th>
                <th scope="col" className="py-2 font-medium text-right">Submissions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700/60">
              {buckets.map((bucket) => (
                <tr key={bucket.key}>
                  <td className="py-2 text-gray-300">{fullLabel(bucket, unit)}</td>
                  <td className="py-2 text-right text-white tabular-nums">{formatCount(bucket.count)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="flex">
          {/* Y axis: only the bounds and the midpoint — the tooltip carries the rest. */}
          <div className="relative w-10 flex-shrink-0 text-right pr-2" style={{ height: PLOT_HEIGHT }}>
            {[axisMax, axisMax / 2, 0].map((tick, i) => (
              <span
                key={tick}
                className="absolute right-2 text-xs text-gray-500 tabular-nums -translate-y-1/2"
                style={{ top: `${i * 50}%` }}
              >
                {formatCount(tick)}
              </span>
            ))}
          </div>

          <div className="flex-1 min-w-0" ref={plotRef}>
            <div className="relative" style={{ height: PLOT_HEIGHT }}>
              {[0, 50, 100].map((top) => (
                <div
                  key={top}
                  className="absolute left-0 right-0"
                  style={{ top: `${top}%`, height: 1, backgroundColor: GRID }}
                />
              ))}

              <div className="absolute inset-0 flex items-end gap-[2px]">
                {buckets.map((bucket, index) => {
                  const height = axisMax ? (bucket.count / axisMax) * 100 : 0;
                  const isActive = active === index;
                  return (
                    <button
                      key={bucket.key}
                      type="button"
                      onMouseEnter={() => setActive(index)}
                      onMouseLeave={() => setActive((cur) => (cur === index ? null : cur))}
                      onFocus={() => setActive(index)}
                      onBlur={() => setActive((cur) => (cur === index ? null : cur))}
                      // The whole column is the hit target, not just the painted
                      // bar. hover:bg-* is required, not cosmetic: App.css has a
                      // global `button:hover` color whose pseudo-class outranks a
                      // plain Tailwind `bg-*`, so an unstated hover turns blue.
                      className="flex-1 min-w-0 h-full flex items-end justify-center bg-transparent hover:bg-purple-500/10 p-0 rounded-t transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-400"
                      aria-label={`${fullLabel(bucket, unit)}: ${bucket.count} submissions`}
                    >
                      {bucket.count > 0 && (
                        <span
                          className="w-full block transition-all duration-150"
                          style={{
                            height: `${height}%`,
                            maxWidth: 24,
                            backgroundColor: DATA_HUE,
                            borderRadius: '4px 4px 0 0',
                            opacity: active === null || isActive ? 1 : 0.55,
                          }}
                        />
                      )}
                    </button>
                  );
                })}
              </div>

              {hovered && (
                <div
                  role="status"
                  className="absolute -top-2 z-10 -translate-x-1/2 -translate-y-full pointer-events-none whitespace-nowrap rounded-lg border border-purple-500/30 bg-gray-900 px-3 py-2 shadow-xl"
                  style={{ left: `${tooltipLeft}%` }}
                >
                  {/* Value leads, label follows: the reader already knows the date. */}
                  <span className="text-base font-semibold text-white">{formatCount(hovered.count)}</span>
                  <span className="text-sm text-gray-400"> submission{hovered.count === 1 ? '' : 's'}</span>
                  <span className="block text-xs text-gray-500">{fullLabel(hovered, unit)}</span>
                </div>
              )}
            </div>

            <div className="relative h-5 mt-2">
              {ticks.map((index) => (
                <span
                  key={buckets[index].key}
                  className="absolute top-0 -translate-x-1/2 text-xs text-gray-500 whitespace-nowrap"
                  style={{ left: `${((index + 0.5) / buckets.length) * 100}%` }}
                >
                  {axisLabel(buckets[index], unit)}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActivityChart;
